import { createFileRoute, useParams, useNavigate } from '@tanstack/react-router'
import { useCreateUser, useUsers, UserMutationError } from '../../../api/queries'
import { Button } from '../../../components/ui/button'
import { Link } from '@tanstack/react-router'
import { mdiArrowLeft } from '@mdi/js'
import { AppIcon } from '../../../components/ui/icon'
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CompanyIdentifierField, validateCompanyIdentifier } from '../../../components/users/CompanyIdentifierField'
import { LEGAL_FORMS } from '../../../constants/company-identifiers'
import { usePropertyDefinitions } from '../../../hooks/useQueries'
import { useCan } from '../../../contexts/PermissionsContext'
import { EnumPropertyInput } from '../../../components/properties/PropertyInputUtils'
import {
  personFields,
  isPhysicalPersonType,
  isLegalEntityType,
  derivePersonName,
  validatePersonRequired,
} from '../../../constants/party-requirements'
import { PageHeader } from '../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../lib/section-icons'
import { CreateContactProperties } from '../../../components/users/CreateContactProperties'

interface UserFormData {
  name: string
  email: string  // Keep as string in form, will be filtered during submission
  type: 'contact' | 'user' | 'user_group' | 'company'
  parent_user_uid?: string
  properties?: Record<string, unknown>
}

export const Route = createFileRoute('/$org/users/create')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => ({
    parent_user_uid: (search.parent_user_uid as string) || undefined,
  }),
})

function RouteComponent() {
  const { org } = useParams({ from: '/$org/users/create' })
  const { parent_user_uid: initialParentUserUid } = Route.useSearch()
  const navigate = useNavigate()
  const { t } = useTranslation('users')

  // Set up breadcrumbs
  useBreadcrumbs([
    { title: t('breadcrumb.home'), path: `/${org}` },
    { title: t('breadcrumb.contacts'), path: `/${org}/users` },
    { title: t('create.breadcrumb') }
  ])

  const [formData, setFormData] = useState<UserFormData>({
    name: '',
    email: '',
    type: 'contact',
    parent_user_uid: initialParentUserUid,
    properties: {}
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [extraProps, setExtraProps] = useState<Record<string, unknown>>({})
  const [extraInvalid, setExtraInvalid] = useState(false)

  // A `user` contact is linked to the account of its address: creating one
  // needs members.write on top of contacts.write, like the backend.
  const canWriteMembers = useCan('members.write')

  // Get users for parent selection
  const { data: users = [] } = useUsers(org, {})

  // The "Role" field below reuses the real contact_role PropertyDefinition
  // (enum: contact/representative/signatory) rather than duplicating its
  // values here — see res-provisioning/types/01-properties.json.
  const { data: userPropertyDefs = [] } = usePropertyDefinitions(org, { entityType: 'user' })
  const contactRoleDef = userPropertyDefs.find((pd) => pd.code === 'contact_role')

  // Create user mutation
  const createUserMutation = useCreateUser(org, {
    onSuccess: (user) => {
      navigate({
        to: '/$org/users/$userUid',
        params: { org, userUid: user.uid }
      })
    },
    onError: (error) => {
      if (error instanceof UserMutationError && error.errorCode === 'EMAIL_ALREADY_EXISTS') {
        setErrors({ email: t('create.error_email_already_exists') })
        return
      }
      setErrors({ general: (error as any)?.title || (error as any)?.message || 'Failed to create user' })
    }
  })

  const handleInputChange = (field: keyof UserFormData, value: string | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  // Set a value under formData.properties (état-civil, siège, etc.) and clear any
  // matching field error.
  const setProp = (key: string, value: string) => {
    setFormData(prev => ({ ...prev, properties: { ...prev.properties, [key]: value } }))
    if (errors[`person_${key}`]) {
      setErrors(prev => ({ ...prev, [`person_${key}`]: '' }))
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    const props = formData.properties || {}

    // Personne physique: the display name is derived from the état-civil fields,
    // so we validate those (tier-0) instead of the free "name" field.
    if (isPhysicalPersonType(formData.type)) {
      Object.assign(newErrors, validatePersonRequired(formData.type, props, t))
    } else if (!formData.name.trim()) {
      // Personne morale / group: the free name (dénomination) is required.
      newErrors.name = t('create.error_name_required')
    }

    // Email is only required for users of type 'user'
    if (formData.type === 'user') {
      if (!formData.email.trim()) {
        newErrors.email = t('create.error_email_required')
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = t('create.error_email_invalid')
      }
    } else if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      // If email is provided for non-user types, validate format
      newErrors.email = t('create.error_email_invalid')
    }

    if (!formData.type) {
      newErrors.type = t('create.error_type_required')
    }

    // Validate company identifier requirement (tier-0 per legal form)
    const identifierError = validateCompanyIdentifier(formData.type, props, t)
    if (identifierError) {
      newErrors[identifierError.field] = identifierError.message
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    // For a personne physique the display name is derived from the état-civil.
    const name = isPhysicalPersonType(formData.type)
      ? derivePersonName(formData.properties || {})
      : formData.name

    // Trim the phone before persisting; an empty string must not be sent as
    // `"phone": ""` (it would still enter the WhatsApp sender-resolution LIKE
    // join and pollute the gap report even though it matches nothing).
    const properties = { ...(formData.properties || {}), ...extraProps }
    const trimmedPhone = String(properties.phone || '').trim()
    if (trimmedPhone) {
      properties.phone = trimmedPhone
    } else {
      delete properties.phone
    }

    const submitData = {
      ...formData,
      name,
      properties,
      email: formData.type === 'user' || formData.email.trim() ? formData.email : undefined,
      parent_user_uid: formData.parent_user_uid || undefined
    }

    createUserMutation.mutate(submitData)
  }

  const handleCancel = () => {
    navigate({ to: '/$org/users', params: { org }, search: {} })
  }

  const parentUsers = users

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link
          to="/$org/users"
          params={{ org }}
          search={{}}
          className="p-2 hover:bg-muted rounded-md"
          data-testid="back-button"
        >
          <AppIcon path={mdiArrowLeft} size="md" />
        </Link>
        <PageHeader
          className="flex-1"
          icon={SECTION_ICONS.users}
          title={t('create.title')}
        />
      </div>

      {/* General Error Display */}
      {errors.general && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
          <div className="text-destructive text-sm">{errors.general}</div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-card rounded-lg border border-border p-6 space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-foreground">{t('create.basic_information')}</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Name / Dénomination — hidden for a personne physique (derived from
                the état-civil block below). */}
            {!isPhysicalPersonType(formData.type) && (
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground mb-1">
                  {isLegalEntityType(formData.type) ? t('create.company.denomination_label') : t('create.name_label')}
                </label>
                <input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`w-full border rounded-md px-3 py-2 bg-background text-foreground ${
                    errors.name ? 'border-destructive' : 'border-border'
                  }`}
                  placeholder={t('create.name_placeholder')}
                  data-testid="name-input"
                />
                {errors.name && (
                  <p className="text-destructive text-sm mt-1">{errors.name}</p>
                )}
              </div>
            )}

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1">
                {formData.type === 'user' ? t('create.email_label_required') : t('create.email_label_optional')}
              </label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`w-full border rounded-md px-3 py-2 bg-background text-foreground ${
                  errors.email ? 'border-destructive' : 'border-border'
                }`}
                placeholder={t('create.email_placeholder')}
                data-testid="email-input"
              />
              {errors.email && (
                <p className="text-destructive text-sm mt-1">{errors.email}</p>
              )}
            </div>

            {/* Type */}
            <div>
              <label htmlFor="type" className="block text-sm font-medium text-foreground mb-1">
                {t('create.type_label')}
              </label>
              <select
                id="type"
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value as 'contact' | 'user' | 'user_group' | 'company')}
                className={`w-full border rounded-md px-3 py-2 bg-background text-foreground ${
                  errors.type ? 'border-destructive' : 'border-border'
                }`}
                data-testid="type-select"
              >
                <option value="contact">{t('types.contact')}</option>
                {canWriteMembers && <option value="user">{t('types.user')}</option>}
                <option value="user_group">{t('types.user_group')}</option>
                <option value="company">{t('types.company')}</option>
              </select>
              {errors.type && (
                <p className="text-destructive text-sm mt-1">{errors.type}</p>
              )}
            </div>

            {/* Parent User — for a personne morale this is the legal
                representative (a personne physique, modelled as the parent). */}
            <div>
              <label htmlFor="parent_user" className="block text-sm font-medium text-foreground mb-1">
                {isLegalEntityType(formData.type)
                  ? t('create.company.representative_label')
                  : t('create.parent_user_label')}
              </label>
              <select
                id="parent_user"
                value={formData.parent_user_uid || ''}
                onChange={(e) => handleInputChange('parent_user_uid', e.target.value || undefined)}
                className="w-full border border-border rounded-md px-3 py-2 bg-background text-foreground"
                data-testid="parent-user-select"
              >
                <option value="">
                  {isLegalEntityType(formData.type) ? t('create.company.no_representative') : t('create.no_parent_user')}
                </option>
                {parentUsers
                  .filter((user) =>
                    // A representative must be a personne physique.
                    isLegalEntityType(formData.type)
                      ? isPhysicalPersonType((user as { type?: string }).type || 'contact')
                      : true
                  )
                  .map((user) => (
                    <option key={user.uid} value={user.uid}>
                      {user.name}{user.email ? ` (${user.email})` : ''}
                    </option>
                  ))}
              </select>
              {isLegalEntityType(formData.type) && (
                <p className="text-xs text-muted-foreground mt-1">{t('create.company.representative_hint')}</p>
              )}
            </div>

            {/* Role relative to the parent company — only meaningful when
                creating a contact under a company (People section on the
                company's page). */}
            {formData.type === 'contact' && formData.parent_user_uid && contactRoleDef?.uid && (
              <div>
                <label htmlFor="contact_role" className="block text-sm font-medium text-foreground mb-1">
                  {t('create.contact_role_label')}
                </label>
                <EnumPropertyInput
                  propertyDefinitionUid={contactRoleDef.uid}
                  value={String(formData.properties?.contact_role || '')}
                  onChange={(value) => setProp('contact_role', value)}
                  org={org}
                  id="contact_role"
                  testId="contact-role-select"
                />
              </div>
            )}
          </div>

          {/* Personne physique — état-civil (tier-0 required, tier-1 recommended). */}
          {isPhysicalPersonType(formData.type) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {personFields().map((f) => {
                const errKey = `person_${f.key}`
                const hasErr = !!errors[errKey]
                return (
                  <div key={f.key}>
                    <label htmlFor={`person-${f.key}`} className="block text-sm font-medium text-foreground mb-1">
                      {t(f.labelKey)}{f.required && <span className="text-destructive"> *</span>}
                    </label>
                    <input
                      id={`person-${f.key}`}
                      type={f.type}
                      value={String(formData.properties?.[f.key] || '')}
                      onChange={(e) => setProp(f.key, e.target.value)}
                      placeholder={f.placeholderKey ? t(f.placeholderKey) : undefined}
                      className={`w-full border rounded-md px-3 py-2 bg-background text-foreground ${
                        hasErr ? 'border-destructive' : 'border-border'
                      }`}
                      data-testid={`person-${f.key}-input`}
                    />
                    {hasErr && <p className="text-destructive text-sm mt-1">{errors[errKey]}</p>}
                  </div>
                )
              })}
            </div>
          )}

          {/* Company-specific: Legal form, Country and Identifier */}
          {formData.type === 'company' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Legal form drives which identifier applies (syndicat → RNC, etc.) */}
              <div>
                <label htmlFor="legal-form" className="block text-sm font-medium text-foreground mb-1">
                  {t('create.legal_form_label')}
                </label>
                <select
                  id="legal-form"
                  value={String(formData.properties?.legalForm || 'company')}
                  onChange={(e) => {
                    const newProps = { ...formData.properties, legalForm: e.target.value }
                    setFormData(prev => ({ ...prev, properties: newProps }))
                  }}
                  className="w-full border border-border rounded-md px-3 py-2 bg-background text-foreground"
                  data-testid="legal-form-select"
                >
                  {LEGAL_FORMS.map((form) => (
                    <option key={form} value={form}>
                      {t(`legal_forms.${form}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-foreground mb-1">
                  {t('create.country_label', { defaultValue: 'Country' })}
                </label>
                <input
                  id="country"
                  type="text"
                  value={String(formData.properties?.country || '')}
                  onChange={(e) => {
                    const newProps = { ...formData.properties, country: e.target.value }
                    setFormData(prev => ({ ...prev, properties: newProps }))
                  }}
                  className="w-full border border-border rounded-md px-3 py-2 bg-background text-foreground"
                  placeholder="fr, gb, us..."
                  data-testid="country-input"
                />
              </div>
              <CompanyIdentifierField
                org={org}
                country={String(formData.properties?.country || '')}
                legalForm={String(formData.properties?.legalForm || 'company')}
                properties={formData.properties || {}}
                onPropertyChange={(code, value) => {
                  const newProps = { ...formData.properties, [code]: value }
                  setFormData(prev => ({ ...prev, properties: newProps }))
                }}
                errors={errors}
                clearError={(field) => setErrors(prev => ({ ...prev, [field]: '' }))}
              />
              {/* Siège social — tier-1 recommended. */}
              <div className="md:col-span-2">
                <label htmlFor="registered-office" className="block text-sm font-medium text-foreground mb-1">
                  {t('create.company.registered_office_label')}
                </label>
                <input
                  id="registered-office"
                  type="text"
                  value={String(formData.properties?.address || '')}
                  onChange={(e) => setProp('address', e.target.value)}
                  className="w-full border border-border rounded-md px-3 py-2 bg-background text-foreground"
                  placeholder={t('create.company.registered_office_placeholder')}
                  data-testid="registered-office-input"
                />
              </div>
            </div>
          )}
        </div>

        {/* Properties */}
        <CreateContactProperties
          org={org}
          definitions={userPropertyDefs}
          excludeCodes={['contact_role', 'phone']}
          onChange={(values, invalid) => { setExtraProps(values); setExtraInvalid(invalid) }}
        />

        {/* Form Actions */}
        <div className="flex justify-end gap-4 pt-6 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={createUserMutation.isPending}
            data-testid="cancel-button"
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={createUserMutation.isPending || extraInvalid}
            data-testid="create-button"
          >
            {createUserMutation.isPending ? t('create.creating') : t('create.create_button')}
          </Button>
        </div>
      </form>
    </div>
  )
}